import type { Permissions } from "@krut/shared";
import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { relative } from "../lib/cron.ts";
import { api, unwrap } from "../api.ts";
import { useMe } from "../hooks.ts";
import { Badge, Button, Card, cx, ErrorText, PageHeader, Spinner } from "../components/ui.tsx";
import { EventBadge, fmtUsd, PhaseBadge, UsageBar } from "../components/runtime.tsx";

export function HomePage() {
  const me = useMe();
  const qc = useQueryClient();
  const rt = useQuery({ queryKey: ["my-runtime"], queryFn: () => unwrap(api.me.runtime.$get()), refetchInterval: 8_000 });
  const usage = useQuery({ queryKey: ["my-usage"], queryFn: () => unwrap(api.me.usage.$get()) });
  const chats = useQuery({ queryKey: ["dash-chats"], queryFn: () => unwrap(api.me.assistant.sessions.$get({ query: { limit: "40", offset: "0" } })) });
  const jobs = useQuery({ queryKey: ["jobs"], queryFn: () => unwrap(api.me.assistant.jobs.$get()) });
  const runs = useQuery({ queryKey: ["recent-runs"], queryFn: () => unwrap(api.me.assistant.runs.$get({ query: { limit: "20" } })), retry: false });
  const restart = useMutation({
    mutationFn: () => unwrap(api.me.runtime.restart.$post()),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["my-runtime"] }),
  });
  if (!me.data) return null;
  const { user, group } = me.data;
  const r = rt.data?.runtime;
  const budget = group?.quotas.monthlyBudgetUsd ?? null;
  const daily = usage.data?.daily ?? [];
  const requests30 = daily.reduce((a, d) => a + d.requests, 0);
  const weekAgo = Date.now() / 1000 - 7 * 86400;
  const chatsThisWeek = (chats.data?.sessions ?? []).filter((c) => c.lastActive >= weekAgo).length;
  const jobList = (jobs.data?.jobs ?? []) as { enabled?: boolean; state?: string }[];
  const activeJobs = jobList.filter((j) => j.enabled !== false && j.state !== "paused").length;
  const runList = runs.data?.runs ?? [];
  const failed = runList.filter((x) => x.status === "failed").length;

  return (
    <>
      <PageHeader title="Dashboard" description={`Welcome back, ${user.name.split(" ")[0]}. Here's what your assistant has been up to.`} />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile label="AI spend this month" value={fmtUsd(r?.spendUsd ?? 0)} sub={budget === null ? "No limit" : `of ${fmtUsd(budget)} budget`} />
        <Tile label="AI requests, last 30 days" value={requests30.toLocaleString()} />
        <Tile label="Chats active this week" value={chats.data ? `${chatsThisWeek}${chats.data.hasMore && chatsThisWeek === chats.data.sessions.length ? "+" : ""}` : "—"} to="/chat" />
        <Tile label="Scheduled tasks" value={String(activeJobs)} sub={runList.length ? `${runList.length - failed} of ${runList.length} recent runs succeeded` : "No runs yet"} to="/tasks" />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <Card className="p-5 lg:col-span-2">
          <DailyChart daily={daily} />
        </Card>
        <Card className="p-5">
          <h2 className="mb-1 text-sm font-semibold">Models used this month</h2>
          <p className="mb-4 text-xs text-[var(--muted)]">Requests per model</p>
          <ModelBars rows={usage.data?.byModel ?? []} />
        </Card>
      </div>

      <Card className="mt-6 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-base font-semibold">Your assistant</h2>
              {r && <PhaseBadge phase={r.phase} />}
            </div>
            <p className="mt-1 text-sm text-[var(--muted)]">
              {rt.isPending ? "" : !rt.data?.enabled ? "Personal assistants are not enabled yet." : !r ? "Being created — this takes a minute or two the first time." : r.message ?? "Ready."}
            </p>
          </div>
          {rt.data?.enabled && (
            <Button disabled={restart.isPending} onClick={() => restart.mutate()}>
              {restart.isPending ? <Spinner /> : null}
              {r ? "Restart" : "Create now"}
            </Button>
          )}
        </div>
        <ErrorText error={restart.error} />
        {r && (
          <div className="mt-6 grid gap-6 sm:grid-cols-3">
            <div>
              <div className="mb-2 text-xs font-medium text-[var(--muted)]">Storage</div>
              <UsageBar used={r.storageUsedBytes} total={r.storageCapacityBytes} />
            </div>
            <div>
              <div className="mb-2 text-xs font-medium text-[var(--muted)]">AI budget this month</div>
              <UsageBar used={r.spendUsd} total={budget} label={budget === null ? `${fmtUsd(r.spendUsd)} · no limit` : `${fmtUsd(r.spendUsd)} of ${fmtUsd(budget)}`} />
            </div>
            <div>
              <div className="mb-2 text-xs font-medium text-[var(--muted)]">Plan</div>
              <div className="flex flex-wrap gap-1.5">
                <Badge tone="accent">{group?.name ?? "No group"}</Badge>
                {r.quotas && <Badge>{r.quotas.memoryMb} MB memory</Badge>}
              </div>
            </div>
          </div>
        )}
      </Card>

      <Activity
        chats={chats.data?.sessions ?? []}
        runs={runList}
        events={rt.data?.events ?? []}
      />

      {group && <Capabilities permissions={group.permissions} />}

      <SharedMemories />
    </>
  );
}

function Tile({ label, value, sub, to }: { label: string; value: string; sub?: string; to?: string }) {
  const body = (
    <Card className={cx("h-full p-4", to && "transition hover:ring-1 hover:ring-[var(--color-accent)]/30")}>
      <div className="text-xs text-[var(--muted)]">{label}</div>
      <div className="mt-1 text-2xl font-semibold tracking-tight">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-[var(--muted)]">{sub}</div>}
    </Card>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

/** One measure at a time (never two scales on one chart): requests or spend per day, last 30 days. */
function DailyChart({ daily }: { daily: { day: string; spendUsd: number; requests: number }[] }) {
  const [measure, setMeasure] = useState<"requests" | "spendUsd">("requests");
  const [hover, setHover] = useState<number | null>(null);
  const days = useMemo(() => {
    const by = new Map(daily.map((d) => [d.day, d]));
    return Array.from({ length: 30 }, (_, i) => {
      const d = new Date(Date.now() - (29 - i) * 86400_000);
      const key = d.toISOString().slice(0, 10);
      return { key, label: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }), requests: by.get(key)?.requests ?? 0, spendUsd: by.get(key)?.spendUsd ?? 0 };
    });
  }, [daily]);
  const max = Math.max(...days.map((d) => d[measure]), measure === "requests" ? 1 : 0.01);
  const fmt = (v: number) => (measure === "requests" ? `${v.toLocaleString()} requests` : fmtUsd(v));
  const h = hover !== null ? days[hover] : null;
  return (
    <div>
      <div className="mb-4 flex items-start gap-3">
        <div className="flex-1">
          <h2 className="text-sm font-semibold">AI use, last 30 days</h2>
          <p className="text-xs text-[var(--muted)]">{h ? `${h.label}: ${fmt(h[measure])}` : "Hover a day for details"}</p>
        </div>
        <div className="flex rounded-lg border border-[var(--line)] p-0.5 text-xs">
          {(["requests", "spendUsd"] as const).map((m) => (
            <button key={m} onClick={() => setMeasure(m)} className={cx("rounded-md px-2.5 py-1", measure === m ? "bg-[var(--panel-2)] font-medium" : "text-[var(--muted)]")}>
              {m === "requests" ? "Requests" : "Spend"}
            </button>
          ))}
        </div>
      </div>
      <div className="relative flex h-40 items-end gap-[2px] border-b border-[var(--line)]" onMouseLeave={() => setHover(null)}>
        {days.map((d, i) => (
          <div key={d.key} className="flex h-full flex-1 items-end" onMouseEnter={() => setHover(i)} title={`${d.label}: ${fmt(d[measure])}`}>
            <div
              className={cx("w-full rounded-t transition-colors", hover === i ? "bg-[var(--color-accent)]" : "bg-[var(--color-accent)]/70")}
              style={{ height: d[measure] ? `${Math.max(3, (d[measure] / max) * 100)}%` : 0 }}
            />
          </div>
        ))}
      </div>
      <div className="mt-1.5 flex justify-between text-[11px] text-[var(--muted)]">
        <span>{days[0]!.label}</span>
        <span>Today</span>
      </div>
    </div>
  );
}

function ModelBars({ rows }: { rows: { model: string; requests: number; spendUsd: number }[] }) {
  if (!rows.length) return <p className="text-sm text-[var(--muted)]">No AI use yet this month.</p>;
  const max = Math.max(...rows.map((r) => r.requests), 1);
  return (
    <ul className="space-y-3">
      {rows.map((r) => (
        <li key={r.model} title={`${r.requests.toLocaleString()} requests · ${fmtUsd(r.spendUsd)}`}>
          <div className="mb-1 flex justify-between gap-2 text-xs">
            <span className="truncate font-mono">{r.model.split("/").pop()}</span>
            <span className="shrink-0 text-[var(--muted)]">{r.requests.toLocaleString()}</span>
          </div>
          <div className="h-2 rounded-full bg-[var(--panel-2)]">
            <div className="h-full rounded-full bg-[var(--color-accent)]/80" style={{ width: `${(r.requests / max) * 100}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

type ActivityItem = { at: number; key: string; icon: string; text: ReactNode; to?: string; badge?: ReactNode };

/** Chats, scheduled-task runs and assistant events, newest first. */
function Activity({
  chats,
  runs,
  events,
}: {
  chats: { id: string; title: string; source: string; lastActive: number }[];
  runs: { id: string; jobName: string; trigger: string; status: string; startedAt: string | null; error: string | null }[];
  events: { id: number; at: string; type: string; message: string }[];
}) {
  const items: ActivityItem[] = [
    ...chats.filter((c) => c.source !== "cron").slice(0, 8).map((c) => ({ at: c.lastActive * 1000, key: `c${c.id}`, icon: "💬", text: c.title, to: `/chat/${c.id}` })),
    ...runs.slice(0, 8).map((r) => ({
      at: r.startedAt ? new Date(r.startedAt).getTime() : 0,
      key: `r${r.id}`,
      icon: "⏱",
      text: <>{r.jobName || "Scheduled task"} <span className="text-[var(--muted)]">· {r.trigger === "manual" ? "run manually" : "scheduled run"}</span></>,
      to: "/tasks",
      badge: <Badge tone={r.status === "completed" ? "good" : r.status === "failed" ? "bad" : "accent"}>{r.status === "completed" ? "Done" : r.status === "failed" ? "Failed" : "Running"}</Badge>,
    })),
    ...events.slice(0, 8).map((e) => ({ at: new Date(e.at).getTime(), key: `e${e.id}`, icon: "⚙️", text: e.message, badge: <EventBadge type={e.type} /> })),
  ]
    .sort((a, b) => b.at - a.at)
    .slice(0, 12);
  return (
    <>
      <h2 className="mt-10 mb-3 text-base font-semibold">Recent activity</h2>
      <Card className="divide-y divide-[var(--line)]">
        {!items.length && <div className="px-5 py-6 text-sm text-[var(--muted)]">Nothing yet — start a chat or schedule a task.</div>}
        {items.map((i) => {
          const row = (
            <div className="flex items-center gap-3 px-5 py-3 text-sm">
              <span className="w-5 shrink-0 text-center">{i.icon}</span>
              <span className="min-w-0 flex-1 truncate">{i.text}</span>
              {i.badge}
              <span className="w-28 shrink-0 text-right text-xs text-[var(--muted)]" title={new Date(i.at).toLocaleString()}>{relative(new Date(i.at).toISOString())}</span>
            </div>
          );
          return i.to ? <Link key={i.key} to={i.to} className="block hover:bg-[var(--panel-2)]">{row}</Link> : <div key={i.key}>{row}</div>;
        })}
      </Card>
    </>
  );
}

const CAPS: { key: "terminal" | "codeExecution" | "browser" | "webTools" | "cronJobs" | "subagents" | "mediaGeneration" | "voice"; label: string }[] = [
  { key: "webTools", label: "Search and read the web" },
  { key: "terminal", label: "Run commands in your workspace" },
  { key: "codeExecution", label: "Run code" },
  { key: "browser", label: "Use a web browser" },
  { key: "cronJobs", label: "Schedule recurring tasks" },
  { key: "subagents", label: "Split work across helper agents" },
  { key: "mediaGeneration", label: "Generate images and video" },
  { key: "voice", label: "Speak replies" },
];

function Capabilities({ permissions }: { permissions: Permissions }) {
  return (
    <>
      <h2 className="mt-10 mb-3 text-base font-semibold">What your assistant can do</h2>
      <Card className="grid gap-x-8 gap-y-2.5 p-5 sm:grid-cols-2">
        {CAPS.map((c) => {
          const on = permissions[c.key];
          return (
            <div key={c.key} className={on ? "flex items-center gap-2.5 text-sm" : "flex items-center gap-2.5 text-sm text-[var(--muted)] line-through decoration-[var(--line)]"}>
              <span className={on ? "grid h-5 w-5 place-items-center rounded-full bg-emerald-500/15 text-xs text-emerald-700" : "grid h-5 w-5 place-items-center rounded-full bg-[var(--panel-2)] text-xs"}>{on ? "✓" : "–"}</span>
              {c.label}
            </div>
          );
        })}
        {permissions.terminal && permissions.cliTools.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 pt-2 text-sm sm:col-span-2">
            <span className="text-[var(--muted)]">Command-line tools:</span>
            {permissions.cliTools.map((t) => <Badge key={t}>{t}</Badge>)}
          </div>
        )}
      </Card>
      <p className="mt-2 text-xs text-[var(--muted)]">Set by your group. Ask an admin if you need something that's switched off.</p>
    </>
  );
}

/** Memories the company-memory rules promoted from this person's assistant; they can take any back. */
function SharedMemories() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["my-promotions"], queryFn: () => unwrap(api.me.memory.promotions.$get()) });
  const withdraw = useMutation({
    mutationFn: (id: number) => unwrap(api.me.memory.promotions[":id"].$delete({ param: { id: String(id) } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["my-promotions"] }),
  });
  if (!q.data || (!q.data.promotionEnabled && !q.data.promotions.length)) return null;
  return (
    <>
      <h2 className="mt-10 mb-1 text-base font-semibold">Shared with your company</h2>
      <p className="mb-3 text-sm text-[var(--muted)]">
        Work knowledge your assistant learned can be shared, without personal details, so colleagues' assistants know it too. Remove anything you'd rather keep private.
      </p>
      <ErrorText error={withdraw.error} />
      <Card className="divide-y divide-[var(--line)]">
        {q.data.promotions.length === 0 && <div className="px-5 py-6 text-sm text-[var(--muted)]">Nothing shared yet.</div>}
        {q.data.promotions.map((p) => (
          <div key={p.id} className="flex items-start gap-4 px-5 py-3 text-sm">
            <div className="min-w-0 flex-1">
              <div>{p.sharedText}</div>
              <div className="mt-0.5 text-xs text-[var(--muted)]">{new Date(p.createdAt).toLocaleDateString()}</div>
            </div>
            <Button disabled={withdraw.isPending} onClick={() => withdraw.mutate(p.id)}>Remove</Button>
          </div>
        ))}
      </Card>
    </>
  );
}
